import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import {
  faFileLines,
  faPlus,
  faSliders
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IArticleType } from '@publishing-engine/domain/models';
import { Button, ButtonText, useToast } from '@morressier/alembic';
import { useState } from 'react';
import { EditArticleTypeModal } from './EditArticleTypeModal';
import { fetchOrgArticleTypes } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import { useOrgArticleType } from 'src/api/mutations';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import Footer from '../../../../../components/Footer';
import ArticleTypeConfigModal from './ArticleTypeConfigModal';


const ArticleTypes = () => {
  const toast = useToast();
  const { orgId } = useWorkspace();

  const { data: articleTypes } = useQuery({
    queryKey: ['organization-article-types', { orgId }],
    queryFn: () => (orgId ? fetchOrgArticleTypes(orgId) : null)
  });

  const articleTypeMutation = useOrgArticleType(orgId);

  const [showArticleModal, setShowArticleModal] = useState({
    isOpen: false,
    articleType: { name: '' }
  });
  const [showConfigModal, setShowConfigModal] = useState({
    isOpen: false,
    articleType: { id: '', name: '', description: '' }
  });

  const onClose = () =>
    setShowArticleModal({ isOpen: false, articleType: { name: '' } });
  const onCloseConfigModal = () =>
    setShowConfigModal({
      isOpen: false,
      articleType: { id: '', name: '', description: '' }
    });

  const onSubmit = async ({ name }: { name: string }) => {
    const res = await articleTypeMutation.mutateAsync({ name });

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `Created ${name}`
      });

      return onClose();
    }

    return toast({
      variant: 'error',
      title: res.message
    });
  };

  const openCreateArticleTypeModal = () => {
    setShowArticleModal({
      isOpen: true,
      articleType: { name: '' }
    });
  };

  return (
    <>
      {showArticleModal.isOpen ? (
        <EditArticleTypeModal
          articleType={showArticleModal.articleType}
          onSave={onSubmit}
          onClose={onClose}
        />
      ) : null}
      {showConfigModal.isOpen ? (
        <ArticleTypeConfigModal
          articleType={showConfigModal.articleType}
          onClose={onCloseConfigModal}
        />
      ) : null}

      <Box id="scrollable-wrapper" overflowY="auto" height="100%">
        <Box height="100%">
          <Box padding={{ base: 2, md: 5 }} minHeight="100%">
            <Flex
              alignItems={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={4}
              width="100%"
              justifyContent={'space-between'}
            >
              <Flex alignItems="center" gap={4}>
                <Text textStyle="headingSection">Article Types</Text>
              </Flex>
              <Box marginLeft="auto">
                <Button
                  iconLeft={<FontAwesomeIcon icon={faPlus} />}
                  onClick={openCreateArticleTypeModal}
                >
                  Create Article Type
                </Button>
              </Box>
            </Flex>

            <Box width="100%" mt={4} overflowX="auto">
              {articleTypes?.items?.length ? (
                <Table>
                  <Thead>
                    <Tr
                      data-testId="article-type-table-header"
                      gap={4}
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black'
                        }
                      }}
                      background="white"
                    >
                      <Th>
                        {' '}
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Name
                        </Text>
                      </Th>

                      <Th></Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {articleTypes?.items?.map((articleType: IArticleType) => (
                      <Tr
                        key={articleType.id}
                        data-testId="article-type-table-row"
                        gap={4}
                        background="white"
                      >
                        <Td textStyle="headingLine">{articleType.name}</Td>

                        <Td textAlign={'end'}>
                          <ButtonText
                            onClick={() =>
                              setShowConfigModal({
                                isOpen: true,
                                articleType: {
                                  id: articleType.id,
                                  name: articleType.name,
                                  description: articleType.description
                                }
                              })
                            }
                          >
                            <FontAwesomeIcon icon={faSliders} />
                          </ButtonText>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <>
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    backgroundColor="neutralSecondaryFill"
                    borderRadius="small"
                    height="112px"
                  >
                    <FontAwesomeIcon icon={faFileLines} fontSize="42px" />
                  </Flex>
                  <Flex flexDirection="column" alignItems="flex-start">
                    <Text textStyle="headingGroup">
                      Create your first Article Type
                    </Text>
                    <Text>
                      Article Types can be reused across all your publications,
                      streamlining your workflow and ensuring consistency.
                    </Text>
                    <Button
                      marginTop={4}
                      variant="secondary"
                      onClick={openCreateArticleTypeModal}
                      iconLeft={<FontAwesomeIcon icon={faPlus} />}
                    >
                      Create Article Type
                    </Button>
                  </Flex>
                </>
              )}
            </Box>
          </Box>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default ArticleTypes;
